import React from "react";
import { Table, Button, Tag, Dropdown, Modal, Empty } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  HistoryOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { tablePagination } from "../../../components";
import { PAGE_SIZE } from "../../../Constant";

interface BankAccountsTableProps {
  data: any[];
  loading: boolean;
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  openTxModal: (record: any, type: "deposit" | "withdrawal") => void;
  openHistoryDrawer: (record: any) => void;
  openAccountDrawer: (record: any, creating: boolean) => void;
  handleDeleteAccount: (id: string) => void;
}

export const BankAccountsTable: React.FC<BankAccountsTableProps> = ({
  data,
  loading,
  total,
  page,
  onPageChange,
  openTxModal,
  openHistoryDrawer,
  openAccountDrawer,
  handleDeleteAccount,
}) => {
  const columns = [
    {
      title: "Bank / Branch",
      key: "bank",
      render: (_: any, rec: any) => (
        <div>
          <span className="font-semibold text-gray-800 text-sm block">
            {rec.bank_name}
          </span>
          <span className="text-xs text-gray-500">
            {rec.bank_branch || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "Account Details",
      key: "details",
      render: (_: any, rec: any) => (
        <div>
          <span className="text-gray-800 font-semibold block">
            {rec.account_name}
          </span>
          <span className="text-xs text-mono text-gray-500 font-mono tracking-wider">
            {rec.account_number}
          </span>
        </div>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (val: any, rec: any) => (
        <span className="font-bold text-sm text-green-600 dark:text-green-400">
          {rec.currency || "LKR"}{" "}
          {Number(val).toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (
        <span className="text-xs text-gray-400">
          {new Date(val).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (_: any, rec: any) => {
        const menuItems = [
          {
            key: "deposit",
            label: "Manual Deposit",
            icon: <ArrowUpOutlined className="text-green-500" />,
            onClick: () => openTxModal(rec, "deposit"),
          },
          {
            key: "withdrawal",
            label: "Manual Withdrawal",
            icon: <ArrowDownOutlined className="text-red-500" />,
            onClick: () => openTxModal(rec, "withdrawal"),
          },
          {
            key: "history",
            label: "Transaction History",
            icon: <HistoryOutlined />,
            onClick: () => openHistoryDrawer(rec),
          },
          {
            type: "divider" as const,
          },
          {
            key: "edit",
            label: "Edit Account",
            icon: <EditOutlined />,
            onClick: () => openAccountDrawer(rec, false),
          },
          {
            key: "delete",
            label: "Delete Account",
            danger: true,
            icon: <DeleteOutlined />,
            onClick: () => {
              Modal.confirm({
                title: "Delete Account?",
                content:
                  "Are you sure you want to delete this bank account? This will hide it from the options.",
                okText: "Delete",
                okButtonProps: { danger: true },
                cancelText: "Cancel",
                onOk: () => handleDeleteAccount(rec.id),
              });
            },
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              className="text-gray-400 hover:text-gray-600"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey="id"
      loading={loading}
      pagination={tablePagination({
        current: page,
        pageSize: PAGE_SIZE,
        total,
        onChange: onPageChange,
        itemName: "accounts",
      })}
      scroll={{ x: 900 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No bank accounts found"
          />
        ),
      }}
    />
  );
};
